import React from 'react';
import styles from './styles.scss';
import moment from 'moment';
import classnames from 'classnames';
import {
  CaptureScheduleModalActionTypes,
  CaptureScheduleModalState,
  Duration,
  Status,
} from '../../types/capture-schedule-modal';

import {
  AppBar,
  AppBarContext,
  AppBarSection,
  AppBarTitle,
  Button,
  ButtonGroup,
  DatePicker,
  InputBox,
  RadioButton,
} from '@densityco/ui';

import {
  calculateEndDate,
  calculateTotalCaptures,
  CAPTURE_WARNING_THRESHOLD,
  parseCaptureDurationInSeconds,
  parseCapturesPerSchedule,
} from '../../utils/calculate-capture-schedules';

import {BackButton, CaptureWarning, NextButton} from './utils';

import {DispatchType} from '../../types/rx-actions';

const {useState, Fragment} = React;


type GlobalConfigurationPage = {
  state: CaptureScheduleModalState,
  dispatch: DispatchType,
  onDismiss: () => void,
};

const GlobalConfigurationPage: React.FunctionComponent<GlobalConfigurationPage> = (props) => {
  const {state, dispatch, onDismiss} = props;

  const [endDateFocused, setEndDateFocused] = useState(false);
  const [captureScheduleEditing, setCaptureScheduleEditing] = useState(false);

  const calculatedTotalCaptures = calculateTotalCaptures(state);
  const calculatedEndDate = calculateEndDate(state);

  return (
      <div className={styles.globalConfigurationWrapper}>
        <AppBarContext.Provider value="CARD_HEADER">
          <AppBar>
            <AppBarTitle>Capture Settings</AppBarTitle>
          </AppBar>
        </AppBarContext.Provider>
        {captureScheduleEditing ? (
            <div className={classnames(styles.globalConfigurationSection, styles.horizontal)}>
              <div className={classnames(styles.formInput, styles.halfWidth)}>
                <label htmlFor="#capture-duration">Capture Duration (in seconds)</label>
                <InputBox
                    type="number"
                    value={state.captureDurationInSeconds}
                    width="100%"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      dispatch({
                        type: CaptureScheduleModalActionTypes.CAPTURE_SCHEDULE_MODAL_CHANGE_CAPTURE_DURATION,
                        captureDuration: e.target.value,
                      });
                    }}
                    id="capture-duration"
                />
              </div>
              <div className={classnames(styles.formInput, styles.halfWidth)}>
                <label htmlFor="#capture-schedule">Captures per schedule</label>
                <InputBox
                    type="number"
                    value={state.capturesPerSchedule}
                    width="100%"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      dispatch({
                        type: CaptureScheduleModalActionTypes.CAPTURE_SCHEDULE_MODAL_CHANGE_CAPTURES_PER_SCHEDULE,
                        capturesPerSchedule: e.target.value,
                      });
                    }}
                    id="capture-duration"
                />
              </div>
            </div>
        ) : (
            <div className={styles.globalConfigurationSection}>
                    <span className={styles.globalConfigurationCapturesPerScheduleSummary}>
                        <strong>{state.capturesPerSchedule}</strong> captures per schedule,
                        each capture is <strong>{state.captureDurationInSeconds}</strong>{' '}
                      seconds long
                    </span>
              <Button
                  onClick={() => setCaptureScheduleEditing(true)}
              >Edit</Button>
            </div>
        )}

        <AppBarContext.Provider value="CARD_HEADER">
          <AppBar>
            <AppBarTitle>Capture Schedule Length</AppBarTitle>
          </AppBar>
        </AppBarContext.Provider>

        <div className={styles.globalConfigurationSection}>
          <RadioButton
              checked={state.duration.type === Duration.TOTAL_CAPTURES}
              text="Total captures per sensor"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                dispatch({
                  type: CaptureScheduleModalActionTypes.CAPTURE_SCHEDULE_MODAL_CHANGE_DURATION_TYPE,
                  durationType: Duration.TOTAL_CAPTURES,
                });
              }}
          />

          <RadioButton
              checked={state.duration.type === Duration.END_DATE}
              text="End Date"
              onChange={() => {
                dispatch({
                  type: CaptureScheduleModalActionTypes.CAPTURE_SCHEDULE_MODAL_CHANGE_DURATION_TYPE,
                  durationType: Duration.END_DATE,
                });
              }}
          />
          <br/>
          <br/>

          {state.duration.type === Duration.TOTAL_CAPTURES ? (
              <div>
                <InputBox
                    type="number"
                    placeholder="30 captures"
                    value={state.duration.type === Duration.TOTAL_CAPTURES ? state.duration.value : ''}
                    width="100%"
                    onChange={(e) => {
                      dispatch({
                        type: CaptureScheduleModalActionTypes.CAPTURE_SCHEDULE_MODAL_CHANGE_DURATION_VALUE,
                        value: e.target.value,
                      });
                    }}
                    disabled={state.duration.type !== Duration.TOTAL_CAPTURES}
                />
                <em className={styles.globalConfigurationCapture}>
                  Calculated End Date:{' '}
                  <span className={styles.globalConfigurationCaptureNumber}>
                                {state.duration.value.length > 0 ? (
                                    calculatedEndDate.format('MM/DD/YYYY')
                                ) : (
                                    <Fragment>&mdash;</Fragment>
                                )}
                            </span>
                </em>
              </div>
          ) : null}
          {state.duration.type === Duration.END_DATE ? (
              <div>
                <DatePicker
                    //placeholder="End Date"
                    //width={130}
                    floating={false}
                    focused={endDateFocused}
                    onFocusChange={focused => setEndDateFocused(focused)}
                    date={
                      state.duration.type === Duration.END_DATE ?
                          state.duration.value : moment.utc()
                    }
                    onChange={(date: moment.Moment) => {
                      dispatch({
                        type: CaptureScheduleModalActionTypes.CAPTURE_SCHEDULE_MODAL_CHANGE_DURATION_VALUE,
                        value: date.startOf('day'),
                      });
                    }}
                />
                <em className={styles.globalConfigurationCapture}>
                  Calculated number of captures per sensor:{' '}
                  <span className={styles.globalConfigurationCaptureNumber}>
                                {calculatedTotalCaptures}
                            </span>
                </em>
              </div>
          ) : null}

          {calculatedTotalCaptures > CAPTURE_WARNING_THRESHOLD ? (
              <div className={styles.globalConfigurationWarning}>
                <CaptureWarning/>
              </div>
          ) : null}
        </div>

        <AppBarContext.Provider value="BOTTOM_ACTIONS">
          <AppBar>
            <AppBarSection/>
            <AppBarSection>
              <ButtonGroup>
                <Button
                    variant="underline"
                    onClick={() => onDismiss()}
                >Cancel</Button>
                <BackButton page={state.page}/>
                <NextButton
                    page={state.page}
                    disabled={
                      !state.duration.value ||
                      !parseCapturesPerSchedule(state) ||
                      !parseCaptureDurationInSeconds(state)
                    }
                />
              </ButtonGroup>
            </AppBarSection>
          </AppBar>
        </AppBarContext.Provider>
      </div>
  );
};
export default GlobalConfigurationPage;

